import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, EmptyState } from "@/components/common";
import { formatINR, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Package, Plus, Zap, Gem, Search, Loader2, Sparkles, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  in_stock: { label: "In Stock", cls: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" },
  low_stock: { label: "Low Stock", cls: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]" },
  out_of_stock: { label: "Out of Stock", cls: "bg-destructive/10 text-destructive" },
};

const EMPTY = { name: "", sku: "", category: "General", quantity: 0, purchase_price: 0, selling_price: 0, supplier: "", industry: "retail", low_stock_threshold: 5, image: "", hsn: "", gst_rate: "", gross_weight: "", net_weight: "", purity: "22K", metal_type: "Gold", making_charges: "", wastage: "", stone_value: "" };

export default function Inventory() {
  const [products, setProducts] = useState(null);
  const [tab, setTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanItems, setScanItems] = useState([]);
  const [scanNotes, setScanNotes] = useState("");
  const [scanBill, setScanBill] = useState(null);
  const [scanSaving, setScanSaving] = useState(false);
  const token = localStorage.getItem("khevora_token");

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image: res.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const scanImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setScanning(true); setScanItems([]); setScanNotes(""); setScanBill(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/inventory/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const items = res.data.items || [];
      setScanItems(items);
      setScanNotes(res.data.notes || "");
      setScanBill(res.data.bill || null);
      if (items.length === 0) toast.error("No products found in that image. Try a clearer screenshot.");
      else toast.success(`Found ${items.length} product${items.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const setScanField = (idx, key, value) =>
    setScanItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  const removeScanItem = (idx) => setScanItems((arr) => arr.filter((_, i) => i !== idx));

  const saveScanned = async () => {
    const items = scanItems
      .filter((it) => it.name?.trim())
      .map((it) => ({
        name: it.name.trim(), category: it.category || "General",
        quantity: +it.quantity || 0, purchase_price: +it.purchase_price || 0,
        selling_price: +it.selling_price || 0, industry: it.industry || "retail",
        sku: it.sku || null, hsn: it.hsn || null, gst_rate: +it.gst_rate || null,
        low_stock_threshold: 5,
      }));
    if (items.length === 0) { toast.error("Nothing to add"); return; }
    setScanSaving(true);
    try {
      const res = await api.post("/products/bulk", { items });
      toast.success(`${res.data.created} products added to inventory`);
      setScanOpen(false); setScanItems([]); setScanNotes(""); setScanBill(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally { setScanSaving(false); }
  };
  const [sellProduct, setSellProduct] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [sell, setSell] = useState({ quantity: 1, payment_method: "cash", customer_id: "walkin", discount: 0 });

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  if (!products) return <Loader label="Loading inventory…" />;

  const filtered = products.filter((p) => {
    if (tab !== "all" && p.industry !== tab) return false;
    if (statusFilter === "sold" && !(p.units_sold > 0)) return false;
    if (statusFilter !== "all" && statusFilter !== "sold" && p.stock_status !== statusFilter) return false;
    if (q && !`${p.name} ${p.sku} ${p.category}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const totalValue = products.reduce((s, p) => s + p.purchase_price * p.quantity, 0);
  const lowCount = products.filter((p) => p.stock_status === "low_stock").length;
  const availableUnits = products.reduce((s, p) => s + (p.quantity || 0), 0);
  const soldUnits = products.reduce((s, p) => s + (p.units_sold || 0), 0);
  const STATUS_FILTERS = [
    { key: "all", label: "All" },
    { key: "in_stock", label: "Available" },
    { key: "low_stock", label: `Low (${lowCount})` },
    { key: "out_of_stock", label: "Out of Stock" },
    { key: "sold", label: "Sold" },
  ];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setAddOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ ...EMPTY, ...p,
      hsn: p.hsn || "", gst_rate: p.gst_rate ?? "",
      gross_weight: p.gross_weight ?? "", net_weight: p.net_weight ?? "",
      making_charges: p.making_charges ?? "", wastage: p.wastage ?? "", stone_value: p.stone_value ?? "",
      purity: p.purity || "22K", metal_type: p.metal_type || "Gold" });
    setAddOpen(true);
  };

  const saveProduct = async () => {
    setSaving(true);
    try {
      const payload = { ...form, quantity: +form.quantity, purchase_price: +form.purchase_price, selling_price: +form.selling_price, low_stock_threshold: +form.low_stock_threshold, gst_rate: form.gst_rate === "" ? null : +form.gst_rate, hsn: form.hsn || null };
      ["gross_weight", "net_weight", "making_charges", "wastage", "stone_value"].forEach((k) => { payload[k] = form[k] === "" || form[k] == null ? null : +form[k]; });
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setAddOpen(false); setForm(EMPTY); setEditingId(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const doSell = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/products/${sellProduct.id}/sell`, {
        quantity: +sell.quantity, payment_method: sell.payment_method,
        customer_id: sell.customer_id === "walkin" ? null : sell.customer_id, discount: +sell.discount,
      });
      toast.success(`Sold! Stock now ${res.data.remaining_stock}. Invoice ${res.data.invoice.invoice_number}`);
      setSellProduct(null); setSell({ quantity: 1, payment_method: "cash", customer_id: "walkin", discount: 0 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sale failed");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Inventory" subtitle={`${products.length} products · ${availableUnits} units available · ${soldUnits} units sold · ${formatINR(totalValue, { compact: true })} in stock`}>
        <Button data-testid="scan-inventory-btn" variant="outline" className="font-semibold border-primary/40 text-primary hover:bg-primary/10" onClick={() => setScanOpen(true)}>
          <Sparkles className="h-4 w-4 mr-1.5" /> Scan to Add
        </Button>
        <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) { setForm(EMPTY); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-product-btn" className="font-semibold" onClick={openAdd}><Plus className="h-4 w-4 mr-1.5" /> Add Product</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Type</Label>
                <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
                  <SelectTrigger data-testid="product-industry" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="retail">Retail</SelectItem><SelectItem value="jewellery">Jewellery</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Product image</Label>
                <div className="mt-1 flex items-center gap-3">
                  {form.image
                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}${form.image}?auth=${token}`} alt="preview" className="h-14 w-14 rounded-lg object-cover border border-border" />
                    : <div className="h-14 w-14 rounded-lg border border-dashed border-border grid place-items-center text-muted-foreground"><Package className="h-5 w-5" /></div>}
                  <label className="cursor-pointer text-sm font-medium text-primary hover:underline">
                    {uploading ? "Uploading…" : form.image ? "Change image" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" data-testid="product-image-input" onChange={uploadImage} disabled={uploading} />
                  </label>
                </div>
              </div>
              <div className="col-span-2"><Label>Name</Label><Input data-testid="product-name" value={form.name} onChange={set("name")} className="mt-1" /></div>
              <div><Label>SKU</Label><Input value={form.sku} onChange={set("sku")} placeholder="Auto" className="mt-1" /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={set("category")} className="mt-1" /></div>
              <div><Label>Quantity</Label><Input type="number" data-testid="product-qty" value={form.quantity} onChange={set("quantity")} className="mt-1" /></div>
              <div><Label>Low-stock alert</Label><Input type="number" value={form.low_stock_threshold} onChange={set("low_stock_threshold")} className="mt-1" /></div>
              <div><Label>Purchase price ₹ <span className="text-muted-foreground font-normal">(cost)</span></Label><Input type="number" data-testid="product-cost-price" value={form.purchase_price} onChange={set("purchase_price")} className="mt-1" /></div>
              <div><Label>Selling price ₹ <span className="text-muted-foreground font-normal">(to customer)</span></Label><Input type="number" data-testid="product-sell-price" value={form.selling_price} onChange={set("selling_price")} className="mt-1" /></div>
              <div><Label>GST %</Label><Input type="number" data-testid="product-gst" value={form.gst_rate} onChange={set("gst_rate")} placeholder="e.g. 5, 12, 18" className="mt-1" /></div>
              <div><Label>HSN code</Label><Input value={form.hsn} onChange={set("hsn")} placeholder="Optional" className="mt-1" /></div>
              <div className="col-span-2"><Label>Supplier</Label><Input value={form.supplier} onChange={set("supplier")} className="mt-1" /></div>
              {form.industry === "jewellery" && (
                <>
                  <div className="col-span-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mt-2"><Gem className="h-3.5 w-3.5" /> Jewellery details</div>
                  <div><Label>Gross weight (g)</Label><Input type="number" value={form.gross_weight} onChange={set("gross_weight")} className="mt-1" /></div>
                  <div><Label>Net weight (g)</Label><Input type="number" value={form.net_weight} onChange={set("net_weight")} className="mt-1" /></div>
                  <div><Label>Purity</Label>
                    <Select value={form.purity} onValueChange={(v) => setForm((f) => ({ ...f, purity: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{["24K", "22K", "18K", "14K", "925"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Metal</Label>
                    <Select value={form.metal_type} onValueChange={(v) => setForm((f) => ({ ...f, metal_type: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{["Gold", "Silver", "Platinum"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Making charges ₹</Label><Input type="number" value={form.making_charges} onChange={set("making_charges")} className="mt-1" /></div>
                  <div><Label>Wastage %</Label><Input type="number" value={form.wastage} onChange={set("wastage")} className="mt-1" /></div>
                  <div className="col-span-2"><Label>Stone value ₹</Label><Input type="number" value={form.stone_value} onChange={set("stone_value")} className="mt-1" /></div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button onClick={saveProduct} disabled={saving || !form.name} data-testid="save-product-btn" className="font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingId ? "Save Changes" : "Save Product")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="retail" data-testid="tab-retail">Retail</TabsTrigger>
            <TabsTrigger value="jewellery" data-testid="tab-jewellery">Jewellery</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="inventory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="pl-9" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`status-filter-${f.key}`}
            onClick={() => setStatusFilter(f.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              statusFilter === f.key
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:bg-secondary"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <SectionCard testid="inventory-table">
        {filtered.length === 0 ? <EmptyState icon={Package} title="No products found" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-semibold py-3">Product</th>
                  <th className="text-left font-semibold py-3 hidden md:table-cell">Category</th>
                  <th className="text-right font-semibold py-3">Available</th>
                  <th className="text-right font-semibold py-3">Sold</th>
                  <th className="text-right font-semibold py-3 hidden sm:table-cell">Cost</th>
                  <th className="text-right font-semibold py-3">Price</th>
                  <th className="text-right font-semibold py-3 hidden lg:table-cell">Value</th>
                  <th className="text-center font-semibold py-3">Status</th>
                  <th className="text-right font-semibold py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors">
                    <td className="py-3">
                      <div className="flex items-center gap-2.5">
                        {p.image
                          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.image}?auth=${token}`} alt={p.name} className="h-9 w-9 rounded-lg object-cover border border-border shrink-0" />
                          : <div className="h-9 w-9 rounded-lg bg-secondary grid place-items-center shrink-0">{p.industry === "jewellery" ? <Gem className="h-4 w-4 text-primary" /> : <Package className="h-4 w-4 text-muted-foreground" />}</div>}
                        <div>
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{p.sku}{p.industry === "jewellery" && p.net_weight ? ` · ${p.net_weight}g ${p.purity}` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 hidden md:table-cell"><Badge variant="secondary">{p.category}</Badge></td>
                    <td className="py-3 text-right font-semibold tabular-nums">{p.quantity}</td>
                    <td className="py-3 text-right tabular-nums">
                      <span className="font-semibold">{p.units_sold || 0}</span>
                      {p.last_sold && <div className="text-[11px] text-muted-foreground">{timeAgo(p.last_sold)}</div>}
                    </td>
                    <td className="py-3 text-right tabular-nums hidden sm:table-cell text-muted-foreground">{formatINR(p.purchase_price, { compact: true })}</td>
                    <td className="py-3 text-right tabular-nums font-medium">{formatINR(p.selling_price, { compact: true })}</td>
                    <td className="py-3 text-right tabular-nums hidden lg:table-cell">{formatINR(p.purchase_price * p.quantity, { compact: true })}</td>
                    <td className="py-3 text-center">
                      <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${STATUS[p.stock_status].cls}`}>{STATUS[p.stock_status].label}</span>
                    </td>
                    <td className="py-3 text-right">
                      <div className="inline-flex items-center gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-btn-${p.id}`} className="font-semibold text-muted-foreground hover:text-primary">
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" disabled={p.quantity <= 0} onClick={() => setSellProduct(p)} data-testid={`sell-btn-${p.id}`} className="font-semibold">
                          <Zap className="h-3.5 w-3.5 mr-1 text-primary" /> Sell
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* AI Scan → Inventory dialog */}
      <Dialog open={scanOpen} onOpenChange={(o) => { setScanOpen(o); if (!o) { setScanItems([]); setScanNotes(""); setScanBill(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="scan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Scan to Inventory</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            Upload a screenshot or photo of your product list, price list or supplier bill. Gemini reads it and fills your items — review, edit and add them all in one click.
          </p>

          {scanItems.length === 0 ? (
            <label className={`mt-2 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 py-12 cursor-pointer hover:bg-primary/10 transition-colors ${scanning ? "pointer-events-none opacity-70" : ""}`}>
              {scanning
                ? <><Loader2 className="h-8 w-8 animate-spin text-primary" /><span className="text-sm font-medium">Reading your image with Gemini…</span></>
                : <><Sparkles className="h-8 w-8 text-primary" /><span className="text-sm font-semibold">Click to upload an image</span><span className="text-xs text-muted-foreground">JPG, PNG or WEBP · max 10MB</span></>}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="scan-file-input" onChange={scanImage} disabled={scanning} />
            </label>
          ) : (
            <div className="mt-2">
              {scanNotes && <p className="text-xs text-muted-foreground mb-2 italic">Gemini: {scanNotes}</p>}

              {scanBill && (scanBill.is_tax_invoice || scanBill.total_tax > 0) && (
                <div className="mb-3 rounded-lg border border-primary/20 bg-primary/5 p-3" data-testid="scan-bill-summary">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-primary">GST Tax Invoice detected</span>
                    {scanBill.invoice_number && <span className="text-xs text-muted-foreground">#{scanBill.invoice_number}{scanBill.invoice_date ? ` · ${scanBill.invoice_date}` : ""}</span>}
                  </div>
                  {(scanBill.supplier_name || scanBill.gstin) && (
                    <div className="text-xs text-muted-foreground mb-2">
                      {scanBill.supplier_name}{scanBill.gstin ? ` · GSTIN ${scanBill.gstin}` : ""}
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Taxable</span><span className="font-medium tabular-nums">{formatINR(scanBill.taxable_total)}</span></div>
                    {scanBill.cgst > 0 && <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="font-medium tabular-nums">{formatINR(scanBill.cgst)}</span></div>}
                    {scanBill.sgst > 0 && <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="font-medium tabular-nums">{formatINR(scanBill.sgst)}</span></div>}
                    {scanBill.igst > 0 && <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="font-medium tabular-nums">{formatINR(scanBill.igst)}</span></div>}
                    <div className="flex justify-between"><span className="text-muted-foreground">Total GST</span><span className="font-semibold tabular-nums text-primary" data-testid="scan-total-gst">{formatINR(scanBill.total_tax)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Grand total</span><span className="font-semibold tabular-nums">{formatINR(scanBill.grand_total)}</span></div>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">Cost shown per item is the pre-GST (taxable) price. Review the GST split above before adding.</p>
                </div>
              )}

              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground bg-secondary/60">
                      <th className="text-left font-semibold p-2">Name</th>
                      <th className="text-left font-semibold p-2 hidden md:table-cell">Category</th>
                      <th className="text-right font-semibold p-2">Qty</th>
                      <th className="text-right font-semibold p-2">Cost ₹</th>
                      <th className="text-right font-semibold p-2">Price ₹</th>
                      <th className="text-right font-semibold p-2">GST %</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanItems.map((it, i) => (
                      <tr key={i} className="border-t border-border/60" data-testid={`scan-row-${i}`}>
                        <td className="p-1.5">
                          <Input value={it.name} onChange={(e) => setScanField(i, "name", e.target.value)} className="h-8" data-testid={`scan-name-${i}`} />
                          {it.hsn ? <span className="text-[10px] text-muted-foreground pl-1">HSN {it.hsn}</span> : null}
                        </td>
                        <td className="p-1.5 hidden md:table-cell"><Input value={it.category} onChange={(e) => setScanField(i, "category", e.target.value)} className="h-8" /></td>
                        <td className="p-1.5 w-20"><Input type="number" value={it.quantity} onChange={(e) => setScanField(i, "quantity", e.target.value)} className="h-8 text-right" /></td>
                        <td className="p-1.5 w-24"><Input type="number" value={it.purchase_price} onChange={(e) => setScanField(i, "purchase_price", e.target.value)} className="h-8 text-right" /></td>
                        <td className="p-1.5 w-24"><Input type="number" value={it.selling_price} onChange={(e) => setScanField(i, "selling_price", e.target.value)} className="h-8 text-right" /></td>
                        <td className="p-1.5 w-16"><Input type="number" value={it.gst_rate} onChange={(e) => setScanField(i, "gst_rate", e.target.value)} className="h-8 text-right" data-testid={`scan-gst-${i}`} /></td>
                        <td className="p-1.5 w-9">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeScanItem(i)} data-testid={`scan-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-3">
                <label className="text-xs font-medium text-primary hover:underline cursor-pointer">
                  {scanning ? "Reading…" : "Scan another image"}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={scanImage} disabled={scanning} />
                </label>
                <span className="text-xs text-muted-foreground">{scanItems.length} items ready</span>
              </div>
            </div>
          )}

          {scanItems.length > 0 && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { setScanItems([]); setScanNotes(""); setScanBill(null); }} className="font-semibold">Clear</Button>
              <Button onClick={saveScanned} disabled={scanSaving} data-testid="scan-save-btn" className="font-semibold">
                {scanSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Add ${scanItems.length} to Inventory`}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Sell dialog */}
      <Dialog open={!!sellProduct} onOpenChange={(o) => !o && setSellProduct(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Quick Sell — {sellProduct?.name}</DialogTitle></DialogHeader>
          {sellProduct && (
            <div className="space-y-4">
              <div className="rounded-lg bg-secondary p-3 flex justify-between text-sm">
                <span className="text-muted-foreground">In stock</span>
                <span className="font-semibold">{sellProduct.quantity} · {formatINR(sellProduct.selling_price)} each</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Quantity</Label><Input type="number" min={1} max={sellProduct.quantity} data-testid="sell-qty" value={sell.quantity} onChange={(e) => setSell((s) => ({ ...s, quantity: e.target.value }))} className="mt-1" /></div>
                <div><Label>Discount ₹</Label><Input type="number" value={sell.discount} onChange={(e) => setSell((s) => ({ ...s, discount: e.target.value }))} className="mt-1" /></div>
              </div>
              <div>
                <Label>Payment method</Label>
                <Select value={sell.payment_method} onValueChange={(v) => setSell((s) => ({ ...s, payment_method: v }))}>
                  <SelectTrigger data-testid="sell-payment" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["cash", "upi", "card", "bank", "credit"].map((m) => <SelectItem key={m} value={m} className="uppercase">{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Customer</Label>
                <Select value={sell.customer_id} onValueChange={(v) => setSell((s) => ({ ...s, customer_id: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walkin">Walk-in Customer</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-between items-center rounded-lg bg-primary/5 border border-primary/20 p-3">
                <span className="text-sm font-medium">Total</span>
                <span className="font-heading text-xl font-bold text-primary">{formatINR(sellProduct.selling_price * sell.quantity - (+sell.discount || 0))}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={doSell} disabled={saving} data-testid="confirm-sell-btn" className="w-full font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Sale & Reduce Stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
